import React, { useState, useEffect } from "react";
import { getCommitteesLedByUser } from "../../api/announcementsAPI";
import { getIGSession, updateIGSession } from "../../api/igSessionAPI";
import { getDecryptedUserId } from "../../utils/cryptoUtils";
import "./IGSessionManager.css";

const IGSessionManager = () => {
  const [committees, setCommittees] = useState([]);
  const [selectedCommittee, setSelectedCommittee] = useState("");
  const [session, setSession] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  // Load user ID and IG committees they lead
  useEffect(() => {
    const loadUserCommittees = async () => {
      try {
        const decryptedUserId = await getDecryptedUserId();
        const ledCommittees = await getCommitteesLedByUser(decryptedUserId);
        const igCommittees = ledCommittees.filter(
          (c) => c.committee_type?.toUpperCase().includes("IG")
        );
        setCommittees(igCommittees);
      } catch (error) {
        console.error("Failed to load committees:", error);
        setStatus("Failed to load your committees. Refresh page.");
      }
    };
    loadUserCommittees();
  }, []);

  // Reset session when committee changes
  useEffect(() => {
    if (!selectedCommittee) return;
    setSession(null);

    const loadIGSession = async () => {
      try {
        setLoading(true);
        const data = await getIGSession(selectedCommittee);

        const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

        setSession({
          is_active: !!data.is_active,
          location: data.location || "",
          day: data.day ? capitalize(data.day) : "",
          start_time: data.start_time ? data.start_time.slice(0, 5) : "",
          end_time: data.end_time ? data.end_time.slice(0, 5) : "",
        });
        setStatus("");
      } catch (error) {
        console.error("Failed to load IG session:", error);
        setStatus("Failed to load IG session details.");
        setSession({
          is_active: false,
          location: "",
          day: "",
          start_time: "",
          end_time: "",
        });
      } finally {
        setLoading(false);
      }
    };

    loadIGSession();
  }, [selectedCommittee]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSession((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCommittee) return;
    try {
      setLoading(true);
      setStatus("");
      await updateIGSession(selectedCommittee, {
        is_active: session.is_active,
        location: session.location,
        day: session.day,
        start_time: session.start_time,
        end_time: session.end_time,
      });
      setStatus("IG session updated successfully!");
    } catch (error) {
      console.error(error);
      setStatus("Failed to update IG session.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ig-session-container">
      <h2>Manage IG Sessions</h2>

      <div className="form-group">
        <label htmlFor="committee">Select IG Committee:</label>
        <select
          id="committee"
          value={selectedCommittee}
          onChange={(e) => setSelectedCommittee(e.target.value)}
        >
          <option value="">-- Select a committee --</option>
          {committees.map((c) => (
            <option key={c.committee_id} value={c.committee_id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {selectedCommittee && loading && <p>Loading IG session...</p>}

      {selectedCommittee && session && !loading && (
        <form onSubmit={handleSubmit} className="ig-session-form">
          <div className="form-group checkbox-group">
            <label htmlFor="is_active" className="checkbox-label">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={session.is_active}
                onChange={handleChange}
              />
              Active
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="location">Location:</label>
            <input
              type="text"
              id="location"
              name="location"
              value={session.location}
              required
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="day">Day of Week:</label>
            <select
              id="day"
              name="day"
              value={session.day}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Day --</option>
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ].map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="start_time">Start Time:</label>
            <input
              type="time"
              id="start_time"
              name="start_time"
              value={session.start_time}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="end_time">End Time:</label>
            <input
              type="time"
              id="end_time"
              name="end_time"
              value={session.end_time}
              onChange={handleChange}
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save IG Session"}
          </button>

          {status && (
            <div
              className={`status-message ${
                status.toLowerCase().includes("success") ? "success" : "error"
              }`}
            >
              {status}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default IGSessionManager;
